import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators, ValidatorFn} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {Subject, takeUntil} from 'rxjs';
import {CustomValidator} from 'src/app/modules/common';
import {DateUtil, convertToUTC} from 'src/app/modules/common/util/DateUtil';
import {ApplicationRowDTO} from 'src/app/modules/onboarding-admin-portal/models/application/ApplicationRowDTO';
import {ReferenceDTO} from 'src/app/modules/onboarding-admin-portal/models/application/ReferenceDTO';
import {DisplayStrings} from '../../../../common/constants/DisplayStrings';
import {SharedService} from '../../../../common/services/shared-service';
import {GlobalValidationError} from '../../utils/ApplicationImportValidationUtil';
import {ApplicationRowReferenceData} from '../obd-bulk-application-import/obd-bulk-application-import.component';
import {formatNumber} from 'src/app/modules/common/util/Util';
import {ApplicationUtil} from '../../utils/ApplicationUtil';

enum Controls {
  ApplicationReferenceNumber = 'applicationReferenceNumber',
  Relationship = 'relationship',
  Title = 'title',
  Name = 'name',
  Surname = 'surname',
  IdType = 'idType',
  IdNumber = 'idNumber',
  DateOfBirth = 'dob',
  Gender = 'gender',
  MaritalStatus = 'maritalStatus',
  Ethnicity = 'ethnicity',
  ContactNumber = 'contactNumber',
  EmailAddress = 'emailAddress',
  BankName = 'bankName',
  AccountNumber = 'accountNumber',
  AccountType = 'accountType',
  AddressType = 'addressType',
  AddressNumber = 'addressNumber',
  AddressName = 'addressName',
  AddressSuburb = 'addressSuburb',
  EmployeeNumber = 'employeeNumber',
  EmploymentDate = 'employmentDate',
  Option = 'option',
  IncomeAmount = 'incomeAmount',
  BrokerCode = 'brokerCode',
  EmployerGroupCode = 'employerGroupCode',
  UploadedBy = 'uploadedBy',
}

@Component({
  selector: 'greydom-obd-bulk-application-row-edit',
  templateUrl: './obd-bulk-application-row-edit.component.html',
  styleUrls: [
    './obd-bulk-application-row-edit.component.scss',
    '../../../../../../styles/global/_dialog-styles.scss',
  ]
})
export class ObdBulkApplicationRowEditComponent implements OnInit, OnDestroy {
  readonly controls = Controls;
  readonly dateFormat: string = DateUtil.dateFormat;
  readonly destroyed$ = new Subject<boolean>();
  readonly BCAP_OPTION_VALUE = 'BCAP';
  // ID validation
  readonly idNumberErrorMessage = DisplayStrings.idErrorMessagesList;
  readonly customValidatorsList = DisplayStrings.customValidatorsList;

  protected readonly CustomValidator = CustomValidator;

  private readonly SID_ID_TYPE = 'SID';

  // Form Validators
  private readonly REQUIRED_VALIDATOR = [Validators.required];
  private readonly ID_NUMBER_VALIDATORS = [Validators.required, CustomValidator.idNumberNumeric, CustomValidator.idNumberLength, CustomValidator.idNumberDOB, CustomValidator.idLuhnValidation];
  private readonly PASSPORT_VALIDATORS = [Validators.required, Validators.minLength(6)];
  private readonly CONTACT_NUMBER_VALIDATORS = [Validators.required, CustomValidator.mobileNumberValidator];
  private readonly EMAIL_VALIDATORS = [Validators.required, CustomValidator.email];
  private readonly INCOME_VALIDATORS = [Validators.required, CustomValidator.twoDecimalPlace];
  private readonly ACCOUNT_NUMBER_VALIDATORS = [Validators.required, CustomValidator.wholeNumber];
  private readonly ADDRESS_NUMBER_VALIDATORS = [Validators.required, CustomValidator.wholeNumber];
  private readonly EMPLOYEE_NUMBER_VALIDATORS = [Validators.required];

  applicationForm: FormGroup;
  heading = 'Edit Application';

  // Reference data
  relationshipTypes: ReferenceDTO[] = [];
  titles: ReferenceDTO[] = [];
  idTypes: ReferenceDTO[] = [];
  genders: ReferenceDTO[] = [];
  maritalStatuses: ReferenceDTO[] = [];
  ethnicities: ReferenceDTO[] = [];
  bankOptions: ReferenceDTO[] = [];
  accountTypes: ReferenceDTO[] = [];
  addressTypes: ReferenceDTO[] = [];
  options: ReferenceDTO[] = [];
  directPayingMemberGroups: ReferenceDTO[] = [];

  constructor(
    private formGroup: FormBuilder,
    private sharedService: SharedService,
    public dialogRef: MatDialogRef<ObdBulkApplicationRowEditComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      applicationRow: ApplicationRowDTO;
      referenceData: ApplicationRowReferenceData;
      isDirectPayingMember: boolean;
    }
  ) {
  }

  ngOnInit(): void {
    this.initForm();
    this.initReferenceData();
    this.initControlSubscriptions();
    if (this.data?.applicationRow) {
      this.setFormValues(this.data.applicationRow);
      this.markAllControlsAsTouched();
    }
    this.updateValidatorsBasedOnValues();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  initForm(): void {
    this.applicationForm = this.formGroup.group({
      [Controls.ApplicationReferenceNumber]: ['', this.REQUIRED_VALIDATOR],
      [Controls.Relationship]: ['', this.REQUIRED_VALIDATOR],
      [Controls.Title]: ['', this.REQUIRED_VALIDATOR],
      [Controls.Name]: ['', this.REQUIRED_VALIDATOR],
      [Controls.Surname]: ['', this.REQUIRED_VALIDATOR],
      [Controls.IdType]: ['', this.REQUIRED_VALIDATOR],
      [Controls.IdNumber]: [''], // Validations Applied Based on ID Type
      [Controls.DateOfBirth]: ['', this.REQUIRED_VALIDATOR],
      [Controls.Gender]: ['', this.REQUIRED_VALIDATOR],
      [Controls.MaritalStatus]: ['', this.REQUIRED_VALIDATOR],
      [Controls.Ethnicity]: ['', this.REQUIRED_VALIDATOR],
      [Controls.ContactNumber]: ['', this.CONTACT_NUMBER_VALIDATORS],
      [Controls.EmailAddress]: ['', this.EMAIL_VALIDATORS],
      [Controls.BankName]: [''], // Conditional validators applied in updateValidatorsBasedOnValues
      [Controls.AccountType]: [''], // Conditional validators applied in updateValidatorsBasedOnValues
      [Controls.AccountNumber]: [''], // Conditional validators applied in updateValidatorsBasedOnValues
      [Controls.AddressType]: ['', this.REQUIRED_VALIDATOR],
      [Controls.AddressNumber]: ['', this.ADDRESS_NUMBER_VALIDATORS],
      [Controls.AddressName]: ['', this.REQUIRED_VALIDATOR],
      [Controls.AddressSuburb]: ['', this.REQUIRED_VALIDATOR],
      [Controls.EmployeeNumber]: [''], // Conditional validators applied in updateValidatorsBasedOnValues
      [Controls.EmploymentDate]: [''], // Conditional validators applied in updateValidatorsBasedOnValues
      [Controls.Option]: [''], // Conditional validators applied in updateValidatorsBasedOnValues
      [Controls.IncomeAmount]: [''] // Conditional validators applied in updateValidatorsBasedOnValues
    });
  }

  initControlSubscriptions(): void {
    // ID Type changes
    this.applicationForm.get(Controls.IdType)?.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        const idNumberControl = this.applicationForm.get(Controls.IdNumber);
        idNumberControl?.setValue(null, { emitEvent: false });
        this.updateValidatorsBasedOnValues();
      });

    // Option changes for income amount
    this.applicationForm.get(Controls.Option)?.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.updateValidatorsBasedOnValues();
      });
  }

  initReferenceData(): void {
    // Use reference data passed in, if available
    if (this.data?.referenceData) {
      const {
        relationshipTypes, titles, idTypes, genders,
        maritalStatuses, ethnicities, bankOptions,
        accountTypes, addressTypes, options,
        directPayingMemberGroups
      } = this.data.referenceData;

      this.relationshipTypes = relationshipTypes || [];
      this.titles = titles || [];
      this.idTypes = idTypes || [];
      this.genders = genders || [];
      this.maritalStatuses = maritalStatuses || [];
      this.ethnicities = ethnicities || [];
      this.bankOptions = bankOptions || [];
      this.accountTypes = accountTypes || [];
      this.addressTypes = addressTypes || [];
      this.options = options || [];
      this.directPayingMemberGroups = directPayingMemberGroups || [];

      return;
    }
  }

  onSave(): void {
    if (this.applicationForm.valid) {
      const formValue = this.applicationForm.value;
      const updatedApplication: ApplicationRowDTO = {
        ...this.data.applicationRow,
        ...formValue,
        [Controls.DateOfBirth]: this.formatDate(formValue[Controls.DateOfBirth]),
        [Controls.IncomeAmount]: this.isDirectPayingMember() ? null : (formValue[Controls.IncomeAmount] ? formatNumber(formValue[Controls.IncomeAmount]) : null),
        [Controls.EmploymentDate]: this.isDirectPayingMember() ? null : this.formatDate(formValue[Controls.EmploymentDate]),
        [Controls.EmployeeNumber]: this.isDirectPayingMember() ? null : formValue[Controls.EmployeeNumber]
      };

      this.dialogRef.close(updatedApplication);
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  getDisplayValue(item: ReferenceDTO): string {
    return item ? `${item.key}: ${item.value}` : '';
  }

  getFormValue(item: ReferenceDTO): string {
    return item ? item.key : '';
  }

  isDirectPayingMember(): boolean {
    return this.data.isDirectPayingMember;
  }

  isBcapOption(): boolean {
    const option = this.applicationForm.get(Controls.Option)?.value;
    return option === this.BCAP_OPTION_VALUE;
  }

  isPrincipalMember(): boolean {
    const relationship = this.applicationForm.get(Controls.Relationship)?.value;
    return relationship == 'SELF';
  }

  getSIDValidators(): ValidatorFn[] {
    return this.isSIDTypeSelected() ? this.ID_NUMBER_VALIDATORS : [];
  }

  getPassportValidators(): ValidatorFn[] {
    return this.isPassportTypeSelected() ? this.PASSPORT_VALIDATORS : [];
  }

  getIncomeAmountValidators(): ValidatorFn[] {
    return this.isBcapOption() ? this.INCOME_VALIDATORS : [];
  }

  getContactNumberValidators(): ValidatorFn[] {
    return this.CONTACT_NUMBER_VALIDATORS;
  }

  getEmailValidators(): ValidatorFn[] {
    return this.EMAIL_VALIDATORS;
  }

  getAddressNumberValidators(): ValidatorFn[] {
    return this.ADDRESS_NUMBER_VALIDATORS;
  }

  getEmployeeNumberValidators(): ValidatorFn[] {
    return this.isPrincipalMember() && !this.isDirectPayingMember() ? this.EMPLOYEE_NUMBER_VALIDATORS : [];
  }

  getAccountNumberValidators(): ValidatorFn[] {
    return this.isPrincipalMember() ? this.ACCOUNT_NUMBER_VALIDATORS : [];
  }

  isSIDTypeSelected(): boolean {
    const idType = this.applicationForm.get(Controls.IdType)?.value;
    return idType?.includes(this.SID_ID_TYPE);
  }

  isPassportTypeSelected(): boolean {
    return !this.isSIDTypeSelected();
  }

  getGlobalValidationError(): string {
    if (!this.data?.applicationRow?.validationErrors || this.data.applicationRow.validationErrors.length === 0) {
      return '';
    }

    const globalErrors = this.data.applicationRow.validationErrors
      .filter(err => err !== GlobalValidationError.GeneralValidation)
      .map(err => DisplayStrings.bulkApplicationGlobalErrorMessagesList[err])
      .filter(Boolean);

    return globalErrors.join('<br>');
  }

  shouldShowError(controlName: string): boolean {
    const control = this.applicationForm.get(controlName);
    return !!(control && control.errors?.['required'] && (control.dirty || control.touched));
  }

  onBlurIncomeAmount(): void {
    const incomeAmount = this.applicationForm.get(Controls.IncomeAmount);
    if (incomeAmount && incomeAmount.value) {
      const valueToFormat = isNaN(incomeAmount.value) ? 0 : incomeAmount.value;
      incomeAmount.setValue(formatNumber(valueToFormat));
    }
  }

  private markAllControlsAsTouched(): void {
    Object.values(Controls).forEach(controlName => {
      const control = this.applicationForm.get(controlName);
      if (control) {
        control.markAsTouched();
        control.markAsDirty();
      }
    });
  }

  private setFormValues(application: ApplicationRowDTO): void {
    const formValues = {
      [Controls.ApplicationReferenceNumber]: application.applicationReferenceNumber,
      [Controls.Relationship]: this.setReferenceValueIfValid(this.relationshipTypes, application.relationship),
      [Controls.Title]: this.setReferenceValueIfValid(this.titles, application.title),
      [Controls.Name]: application.name,
      [Controls.Surname]: application.surname,
      [Controls.IdType]: this.setReferenceValueIfValid(this.idTypes, application.idType),
      [Controls.IdNumber]: application.idNumber,
      [Controls.DateOfBirth]: application.dob ? convertToUTC(application.dob) : null,
      [Controls.Gender]: this.setReferenceValueIfValid(this.genders, application.gender),
      [Controls.MaritalStatus]: this.setReferenceValueIfValid(this.maritalStatuses, application.maritalStatus),
      [Controls.Ethnicity]: this.setReferenceValueIfValid(this.ethnicities, application.ethnicity),
      [Controls.ContactNumber]: application.contactNumber,
      [Controls.EmailAddress]: application.emailAddress,
      [Controls.BankName]: this.setReferenceValueIfValid(this.bankOptions, application.bankName),
      [Controls.AccountNumber]: application.accountNumber,
      [Controls.AccountType]: this.setReferenceValueIfValid(this.accountTypes, application.accountType),
      [Controls.AddressType]: this.setReferenceValueIfValid(this.addressTypes, application.addressType),
      [Controls.AddressNumber]: application.addressNumber,
      [Controls.AddressName]: application.addressName,
      [Controls.AddressSuburb]: application.addressSuburb,
      [Controls.EmployeeNumber]: application.employeeNumber,
      [Controls.EmploymentDate]: application.employmentDate ? convertToUTC(application.employmentDate) : null,
      [Controls.Option]: this.setReferenceValueIfValid(this.options, application.option),
      [Controls.IncomeAmount]: application.incomeAmount,
      [Controls.BrokerCode]: application.brokerCode,
      [Controls.EmployerGroupCode]: application.employerGroupCode,
      [Controls.UploadedBy]: application.uploadedBy,
    };

    this.applicationForm.patchValue(formValues);

    this.updateValidatorsBasedOnValues();
  }

  private updateValidatorsBasedOnValues(): void {
    const idNumberControl = this.applicationForm.get(Controls.IdNumber);
    const incomeAmountControl = this.applicationForm.get(Controls.IncomeAmount);
    const employmentDateControl = this.applicationForm.get(Controls.EmploymentDate);
    const employeeNumberControl = this.applicationForm.get(Controls.EmployeeNumber);
    const optionControl = this.applicationForm.get(Controls.Option);
    const bankNameControl = this.applicationForm.get(Controls.BankName);
    const accountNumberControl = this.applicationForm.get(Controls.AccountNumber);
    const accountTypeControl = this.applicationForm.get(Controls.AccountType);

    // -------------------------
    // ID Type-based validators
    // -------------------------
    if (this.isSIDTypeSelected()) {
      idNumberControl.clearValidators();
      idNumberControl.setValidators(this.ID_NUMBER_VALIDATORS);
    }
    else if (this.isPassportTypeSelected()) {
      idNumberControl.clearValidators();
      idNumberControl.setValidators(this.PASSPORT_VALIDATORS);
    }
    idNumberControl.updateValueAndValidity({ emitEvent: false }); // Do not emit event to prevent infinite recursion loop situation

    // -------------------------
    // Income field validation for BCAP option
    // -------------------------
    if (this.isPrincipalMember() && this.isBcapOption()) {
      incomeAmountControl.setValidators(this.INCOME_VALIDATORS);
    }
    else {
      incomeAmountControl.clearValidators();
    }
    incomeAmountControl.updateValueAndValidity({ emitEvent: false }); // Do not emit event to prevent infinite recursion loop situation

    // -------------------------
    // Conditional validators for non-DPM (Direct Paying Member)
    // -------------------------
    if (this.isPrincipalMember() && !this.isDirectPayingMember()) {
      employeeNumberControl.setValidators(this.EMPLOYEE_NUMBER_VALIDATORS);
      employmentDateControl.setValidators(this.REQUIRED_VALIDATOR);
    }
    else {
      employeeNumberControl.clearValidators();
      employmentDateControl.clearValidators();
    }
    employeeNumberControl.updateValueAndValidity({ emitEvent: false }); // Do not emit event to prevent infinite recursion loop situation
    employmentDateControl.updateValueAndValidity({ emitEvent: false }); // Do not emit event to prevent infinite recursion loop situation

    // -------------------------
    // Option and bank fields for non-principal members
    // -------------------------
    if (this.isPrincipalMember()) {
      optionControl.setValidators(this.REQUIRED_VALIDATOR);
      bankNameControl.setValidators(this.REQUIRED_VALIDATOR);
      accountNumberControl.setValidators(this.ACCOUNT_NUMBER_VALIDATORS);
      accountTypeControl.setValidators(this.REQUIRED_VALIDATOR);
    }
    else {
      optionControl.clearValidators();
      bankNameControl.clearValidators();
      accountNumberControl.clearValidators();
      accountTypeControl.clearValidators();
    }
    optionControl.updateValueAndValidity({ emitEvent: false }); // Do not emit event to prevent infinite recursion loop situation
    bankNameControl.updateValueAndValidity({ emitEvent: false }); // Do not emit event to prevent infinite recursion loop situation
    accountNumberControl.updateValueAndValidity({ emitEvent: false }); // Do not emit event to prevent infinite recursion loop situation
    accountTypeControl.updateValueAndValidity({ emitEvent: false }); // Do not emit event to prevent infinite recursion loop situation
  }

  private formatDate(date: Date): string {
    if (date) {
      return this.sharedService.formatDate(date, this.dateFormat);
    }
    else {
      return '';
    }
  }

  private setReferenceValueIfValid(referenceDataArray: ReferenceDTO[], referenceValue: string): string {
    if (!referenceDataArray || !referenceValue) {
      return null;
    }
    const referenceCode = ApplicationUtil.getCodeFromString(referenceValue);
    const foundReference = referenceDataArray.find(
      (reference) => reference.key === referenceCode
    );
    return foundReference ? referenceCode : null;
  }

}
